import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Truck, ArrowRight, CheckCircle, Phone, FileText, Shield, Wrench, Receipt, Monitor } from 'lucide-react';

const commonDeductions = [
  'Truck fuel',
  'Repairs and maintenance',
  'Oil, DEF, fluids, and tires',
  'Truck washes',
  'Insurance for the truck, trailer, cargo, liability, bobtail/non-trucking, occupational accident, etc.',
  'Lease payments or depreciation if you own the truck or equipment',
  'Interest on business vehicle loans for the business-use portion',
  'Licenses, permits, tags, and IFTA-related fees',
  'Tolls, scales, and parking',
  'Dispatch fees, load-board fees, and factoring fees',
  'Accounting, tax preparation, bookkeeping, and legal fees',
  'Cell phone and internet used for business',
  'Office supplies, printer, paper, and postage',
  'ELD, GPS, dash cam, logging software, and routing software',
  'Lodging when traveling away from your tax home overnight for business',
  'Business meals while traveling; in most cases the deduction is 50%, but interstate truck operators subject to DOT hours-of-service limits may deduct 80% of qualifying business meals',
];

const missedDeductions = [
  'PrePass, EZ Pass, and toll transponder fees',
  'CB radio and communication equipment',
  'Safety gear such as gloves, steel-toe boots, reflective vests, rain gear, hard hats, and goggles when job-related',
  'Bedding, mini-fridge, microwave, cooler, inverter, and power cords for the sleeper berth if used for the business of over-the-road driving',
  'Load locks, straps, chains, binders, tarps, and bungees',
  'Flashlights, tools, fire extinguisher, and first-aid kits',
  'Drug testing, DOT physicals, and medical card fees when directly tied to operating the business',
  'Occupational and trade association dues',
  'Continuing education and CDL renewal-related business costs',
  'Truck stop showers and laundry while away overnight on business, if ordinary, necessary, and documented',
  'Bank fees on a dedicated business account',
  'Merchant processing fees if you accept direct payments',
  'Business use of home if you have a qualifying home office used regularly and exclusively for dispatching, recordkeeping, invoicing, compliance, and administrative work',
  'Health insurance for self-employed drivers may be deductible separately on Form 7206 and Schedule 1, subject to the rules',
  'Section 179 or depreciation for qualifying equipment such as a truck, trailer, ELD hardware, office equipment, and certain other business assets',
];

const buckets = [
  { icon: Truck, title: 'Truck Operating Costs', description: 'Fuel, repairs, maintenance, tires, washes, insurance, lease payments, and depreciation.' },
  { icon: Receipt, title: 'Travel Costs', description: 'Lodging, meals (50% or 80% for DOT drivers), tolls, scales, parking, and showers.' },
  { icon: Shield, title: 'Compliance & Safety Costs', description: 'Licenses, permits, IFTA fees, DOT physicals, drug testing, safety gear, and CDL renewals.' },
  { icon: Monitor, title: 'Admin & Technology Costs', description: 'ELD, GPS, dash cam, cell phone, internet, office supplies, accounting, and legal fees.' },
  { icon: FileText, title: 'Owner-Level Deductions', description: 'Health insurance, Section 179 depreciation, home office, and retirement contributions.' },
];

export default function TruckDriverTax() {
  useSEO({
    title: 'Truck Driver & Owner-Operator Tax Help',
    description: 'Tax deductions and preparation for owner-operators, independent contractors, and truck drivers. Maximize deductions, organize recordkeeping, and stay compliant.',
  });

  return (
    <div data-testid="truck-driver-tax-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Truck Driver & Owner-Operator Tax Help
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Tax Deductions Built for the Road
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Owner-operators and independent contractors leave thousands of dollars on the table every year. We help you identify every legitimate deduction, organize your recordkeeping, and file with confidence.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ask">
                <Button
                  data-testid="truck-hero-cta-ask"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Ask a Tax Question - Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="truck-hero-cta-book"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-md"
                >
                  Book a Consultation
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Common Deductions */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Most Common Deductions
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4 max-w-xl">
              Deductions every owner-operator should know
            </h2>
            <p className="text-base text-slate-500 mb-10 max-w-xl">
              For an owner-operator or independent contractor, these are the most common deductions:
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {commonDeductions.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-brand-green mt-1 flex-shrink-0" />
                  <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Often-Missed Deductions */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Often Missed
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4 max-w-xl">
              Less-common deductions many drivers overlook
            </h2>
            <p className="text-base text-slate-500 mb-10 max-w-xl">
              These are the ones that can add up to significant savings at tax time:
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {missedDeductions.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-lg">
                  <Wrench className="w-4 h-4 text-brand-navy mt-1 flex-shrink-0" />
                  <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Recordkeeping */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Recordkeeping That Matters
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
                The IRS cares about documentation
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-6">
                For truck drivers, the IRS is especially focused on time, place, amount, and business purpose. For travel, meals, and vehicle-related items, you should keep logs, receipts, trip sheets, ELD records, settlement statements, and proof of payment.
              </p>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-6">
                Lodging generally requires receipts regardless of amount, and many other expenses need receipts when they are $75 or more. Electronic records are acceptable if maintained properly.
              </p>
              <Link to="/ask">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold rounded-md">
                  Get Help With Recordkeeping <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <h3 className="font-heading font-semibold text-lg text-brand-navy mb-6">
                5 Buckets for Organizing Truck Driver Deductions
              </h3>
              <div className="space-y-4">
                {buckets.map((bucket, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 border border-slate-200 rounded-lg">
                    <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center flex-shrink-0">
                      <bucket.icon className="w-5 h-5 text-brand-navy" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-sm text-brand-navy mb-1">{bucket.title}</h4>
                      <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{bucket.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white mb-4">
              Need help with your truck driver taxes?
            </h2>
            <p className="text-base text-slate-400 mb-8 max-w-lg mx-auto">
              If you need help setting up your recordkeeping system, we can assist you with organizing your deductions so your income and expenses are easier to track throughout the year. Proper recordkeeping can also make tax preparation faster, cleaner, and less stressful at filing time.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Ask a Tax Question - Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
